import axiosInstance from '../../services/axiosConfig';
import type { BillingInterval } from './planDetails';

export interface MembershipPerk {
  perk_id: string;
  label: string;
  sort_order: number;
}

export interface MembershipPlan {
  plan_id: string;
  tier: 'bronze' | 'silver' | 'gold';
  name: string;
  subtitle: string;
  icon_key: string;
  theme: 'bronze' | 'silver' | 'gold';
  featured: boolean;
  includes_previous_tier: boolean;
  previous_tier_banner: string;
  currency: string;
  monthly_amount: number;
  annual_amount: number;
  annual_savings_percent?: number;
  service_discount_percent: number;
  cta_label: string;
  perks: MembershipPerk[];
}

export interface MembershipCatalogSettings {
  annual_save_badge: string;
  page_title: string;
  page_subtitle: string;
  hub_none_title?: string;
  hub_none_body?: string;
  hub_none_bullets?: string[];
  hub_bronze_upgrade_title?: string;
  hub_bronze_upgrade_body?: string;
  hub_bronze_upgrade_bullets?: string[];
  hub_silver_upgrade_title?: string;
  hub_silver_upgrade_body?: string;
  hub_silver_upgrade_bullets?: string[];
  hub_gold_title?: string;
  hub_gold_body?: string;
}

export interface MembershipCatalogResponse {
  success: boolean;
  enabled: boolean;
  settings: MembershipCatalogSettings | null;
  plans: MembershipPlan[];
}

export interface MembershipCheckoutResponse {
  success: boolean;
  sessionId: string;
  sessionUrl: string;
  amount: number;
  currency: string;
  billing: BillingInterval;
  plan: {
    plan_id: string;
    name: string;
    tier: string;
  };
  message?: string;
}

export async function fetchMembershipCatalog(): Promise<MembershipCatalogResponse> {
  const { data } = await axiosInstance.get<MembershipCatalogResponse>('/api/membership/plans');
  return data;
}

export async function startMembershipCheckout(input: {
  plan_id: string;
  billing: BillingInterval;
  success_url: string;
  cancel_url: string;
}): Promise<MembershipCheckoutResponse> {
  const { data } = await axiosInstance.post<MembershipCheckoutResponse>(
    '/api/membership/checkout',
    input
  );
  return data;
}

export interface MyMembershipEnrollment {
  client_id: string;
  plan_id: string;
  tier: 'bronze' | 'silver' | 'gold';
  billing: BillingInterval;
  status: 'active' | 'past_due' | 'cancelled' | 'expired' | 'incomplete';
  amount: number;
  currency: string;
  activated_at: string | null;
  current_period_end: string | null;
  months_remaining: number;
  days_remaining: number;
  cancel_at_period_end: boolean;
  service_discount_percent?: number;
}

export interface MyMembershipResponse {
  success: boolean;
  status: 'none' | MyMembershipEnrollment['status'];
  enrollment: MyMembershipEnrollment | null;
}

export async function fetchMyMembership(): Promise<MyMembershipResponse> {
  const { data } = await axiosInstance.get<MyMembershipResponse>('/api/membership/me');
  return data;
}
